import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import type { SearchResultsFact } from '@/analytics/models/cventAnalytics/SearchResultsFact';
import { getPageUrl } from '@/utils/analyticsClientHelper';
import { filterOut4DiamondAdsMax, mergeCompAndVideoAds } from '@/utils/venueListHelper';

type BasicAdInfo = {
  code: string | undefined;
  diamondLevel: string | undefined;
  listingCode: string | undefined;
  featuredLevel: string | undefined;
  position: number;
};

type AdId = {
  venueId?: string | null;
  microsite_id?: string;
  microsite_page_id?: string;
};

type FastResponder = {
  venueId: string;
  code: string | undefined;
  diamondLevel: string | undefined;
  position: number;
  ad_name?: string | null;
  ad_code?: string | null;
};

const OFFSET = 1;

function handleMicrosite(venue: CsnSearchVenue): AdId {
  const isMicrosite = venue.listingOwnerType === 'MICROSITE';
  return isMicrosite
    ? {
        microsite_id: venue.id,
        microsite_page_id: venue.pageId,
        venueId: null
      }
    : {
        venueId: venue.id
      };
}

type AdData = BasicAdInfo & AdId;

function basicInfo(venue: CsnSearchVenue, position: number): AdData {
  return {
    code: venue.code,
    diamondLevel: venue.diamondLevel,
    listingCode: venue.listingCode,
    featuredLevel: venue.featuredLevel,
    position: position + OFFSET,
    ...handleMicrosite(venue)
  };
}

function getFastResponderVenues(venue: CsnSearchVenue, position: number): FastResponder {
  if (venue.featuredLevel) {
    return {
      venueId: venue.id,
      code: venue.code,
      diamondLevel: venue.diamondLevel,
      position: position + OFFSET,
      ad_name: venue.featuredLevel,
      ad_code: venue.listingCode
    };
  }
  return {
    venueId: venue.id,
    code: venue.code,
    diamondLevel: venue.diamondLevel,
    position: position + OFFSET
  };
}

export type Ads = {
  four_diamond_ads: AdData[];
  diamond_plus_ad: AdData[];
  diamond_plus_brand_ad: AdData[];
  video_ad: AdData[];
  competitive_market_ads: AdData[];
};

export default function getSearchResultsFact(
  isMapExpanded: boolean,
  searchTerm: string,
  page: number,
  total: number,
  venues: CsnSearchVenue[],
  competitiveMarketVenues: CsnSearchVenue[],
  videoVenues: CsnSearchVenue[],
  isFastResponderEnabled: number,
  isAIMode?: string
): SearchResultsFact {
  const venuesToLog = filterOut4DiamondAdsMax(venues);
  const organicResults: (BasicAdInfo & AdId)[] = [];
  const ads: Ads = {
    four_diamond_ads: [],
    diamond_plus_ad: [],
    diamond_plus_brand_ad: [],
    video_ad: [],
    competitive_market_ads: []
  };

  const fastResponderVenues: FastResponder[] = [];

  venuesToLog.forEach((venue, index) => {
    if (venue.featuredLevel === undefined) {
      organicResults.push(basicInfo(venue, organicResults.length));
    } else if (venue.featuredLevel === 'FOUR_DIAMOND') {
      ads.four_diamond_ads.push(basicInfo(venue, ads.four_diamond_ads.length));
    } else if (venue.featuredLevel === 'DIAMOND_PLUS') {
      ads.diamond_plus_ad.push(basicInfo(venue, ads.diamond_plus_ad.length));
    } else if (venue.featuredLevel === 'DIAMOND_PLUS_BRAND') {
      ads.diamond_plus_brand_ad.push(basicInfo(venue, ads.diamond_plus_brand_ad.length));
    }
    if (isFastResponderEnabled && venue.fastResponder) fastResponderVenues.push(getFastResponderVenues(venue, index));
  });

  const mergedAds = mergeCompAndVideoAds(competitiveMarketVenues, videoVenues);

  // video ads
  mergedAds.forEach((venue, index) => {
    if (venue.adVideoUrl) {
      ads.video_ad.push(basicInfo(venue, index));
      if (isFastResponderEnabled && venue.fastResponder) fastResponderVenues.push(getFastResponderVenues(venue, index));
    }
  });

  // competitive ads
  mergedAds.forEach((venue, index) => {
    if (!venue.adVideoUrl) {
      ads.competitive_market_ads.push(basicInfo(venue, index));
      if (isFastResponderEnabled && venue.fastResponder) fastResponderVenues.push(getFastResponderVenues(venue, index));
    }
  });

  // old structure :(
  const adsJSON = {
    ...ads,
    competitive_market_ads: ads.competitive_market_ads.length > 0 ? ads.competitive_market_ads : null,
    video_ad: ads.video_ad.length > 0 ? ads.video_ad : null
  };
  if (isFastResponderEnabled)
    return {
      type: 'csn_search_results',
      page: 'search_results',
      current_page_number: page,
      total_search_results: total,
      ads: JSON.stringify(adsJSON),
      venue_listing: JSON.stringify(organicResults),
      search_term: searchTerm,
      map_view: isMapExpanded ? 'EXPANDED' : 'COLLAPSED',
      page_url: getPageUrl(),
      search: isAIMode === 'true' ? 'AI_SEARCH' : 'CLASSIC_SEARCH',
      fast_responder_venues: JSON.stringify(fastResponderVenues)
    };
  return {
    type: 'csn_search_results',
    page: 'search_results',
    current_page_number: page,
    total_search_results: total,
    ads: JSON.stringify(adsJSON),
    venue_listing: JSON.stringify(organicResults),
    search_term: searchTerm,
    map_view: isMapExpanded ? 'EXPANDED' : 'COLLAPSED',
    page_url: getPageUrl(),
    search: isAIMode === 'true' ? 'AI_SEARCH' : 'CLASSIC_SEARCH'
  };
}
